import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight, Star, Phone, BadgeCheck, MapPin, ListChecks, UserRound, PhoneCall,
  Check, Wallet, CalendarCheck, ShieldCheck, Timer,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import ReviewsSection, { LeaveReviewButton, useReviewStats } from "@/components/ReviewsSection";
import CitiesCovered from "@/components/CitiesCovered";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { useSeo } from "@/hooks/useSeo";
import SEO_DATA from "@/seo/seoPages.json";

export const HERO_DEFAULT_IMG =
  "https://images.unsplash.com/photo-1597724939962-4bd5088797d8?auto=format&fit=crop&w=1000&q=80";
const REASSURE_IMG =
  "https://images.pexels.com/photos/8550826/pexels-photo-8550826.jpeg?auto=compress&cs=tinysrgb&w=1000";

// Libellé UNIQUE de l'action principale, partout sur le site élève.
export const MAIN_CTA = "Trouver ma place gratuitement";

function Header() {
  return (
    <header className="sticky top-0 z-30 border-b border-neutral-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
        <Link to="/"><Logo className="h-10" /></Link>
        <nav className="hidden items-center gap-6 text-sm font-semibold text-neutral-600 md:flex">
          <a href="#fonctionnement" className="hover:text-brand">Comment ça marche</a>
          <a href="#tarifs" className="hover:text-brand">Prix</a>
          <a href="#faq" className="hover:text-brand">Questions</a>
          <Link to="/bon-carburant" data-testid="nav-bon-carburant" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 font-bold text-emerald-700 hover:bg-emerald-100">Bon d'achat 25 €</Link>
        </nav>
        <div className="flex items-center gap-3 text-sm font-semibold">
          <Link to="/contact" data-testid="nav-contact" className="hidden items-center gap-1 text-neutral-600 hover:text-brand lg:inline-flex"><Phone className="h-4 w-4" /> Contact</Link>
          <Link to="/connexion" data-testid="nav-login" className="hidden text-neutral-500 hover:text-brand sm:inline">Espace pro</Link>
          <Link to="/reserver" data-testid="nav-cta" className="rounded-xl bg-brand px-4 py-2 text-white hover:bg-brand-hover">Trouver ma place</Link>
        </div>
      </div>
    </header>
  );
}

// Étape du parcours élève
const Step = ({ n, Icon, title, sub }) => (
  <li className="relative rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm">
    <div className="flex items-center gap-3">
      <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand/10 text-brand"><Icon className="h-5 w-5" /></span>
      <span className="text-xs font-bold uppercase tracking-wider text-brand">Étape {n}</span>
    </div>
    <h3 className="mt-4 text-lg font-black text-ink">{title}</h3>
    <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{sub}</p>
  </li>
);

const FAQ = ({ q, children, testId }) => (
  <details className="group border-b border-neutral-200 py-4" data-testid={testId}>
    <summary className="flex cursor-pointer list-none items-center justify-between text-left font-bold text-ink">
      {q}
      <span className="ml-4 text-xl leading-none text-brand transition-transform group-open:rotate-45">+</span>
    </summary>
    <p className="mt-3 text-sm leading-relaxed text-neutral-600">{children}</p>
  </details>
);

const CtaButton = ({ testId, className = "", to = "/reserver" }) => (
  <Link to={to} data-testid={testId}
    className={`inline-flex items-center justify-center gap-2 rounded-xl bg-brand px-7 py-4 text-base font-bold text-white shadow-lg shadow-brand/20 transition hover:bg-brand-hover ${className}`}>
    {MAIN_CTA} <ArrowRight className="h-5 w-5" />
  </Link>
);

// À qui on s'adresse — ORDRE DE PRIORITÉ (le 1er est mis en avant).
const AUDIENCES = [
  { id: "retake", emoji: "🔁", title: "Tu as raté ton examen", testId: "home-retake-cta",
    text: "Repasse vite avec une formule courte : entraînement sur le parcours d'examen et date rapide.",
    cta: "Repasser vite", to: "/repasser-permis" },
  { id: "points", emoji: "♻️", title: "Tu as perdu tous tes points",
    text: "Permis annulé ou invalidé ? Repasse code et conduite à la suite, sans attendre des mois.",
    cta: "Tout repasser vite", to: "/reserver" },
  { id: "urgent", emoji: "⚡", title: "Tu as une urgence",
    text: "Nouveau job, voyage, naissance… Enchaîne heures de conduite et examen en quelques semaines.",
    cta: "Trouver une place", to: "/reserver" },
  { id: "driver", emoji: "🚗", title: "Tu sais déjà conduire",
    text: "Appris en double commande ou en conduite accompagnée, mais fatigué d'attendre une date d'examen ?",
    cta: "Avoir une date vite", to: "/reserver?parcours=repassage" },
];

const cityLabel = (slug) => (slug || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join("-");

// FAQ : le même texte alimente l'affichage ET les données structurées FAQPage (exigence Google).
const buildFaq = (cityName) => [
  ["faq-accelere", "Qu'est-ce que le permis accéléré ?",
    `Le permis accéléré (ou stage intensif) regroupe tes heures de conduite sur quelques jours ou quelques semaines au lieu de plusieurs mois. Le programme est le même que celui d'une formation classique, avec un enseignant diplômé, mais à un rythme soutenu qui t'évite d'oublier d'une leçon à l'autre${cityName ? `. Nos auto-écoles partenaires proposent ce format à ${cityName}.` : "."}`],
  ["faq-duree", "Combien de temps dure un permis accéléré ?",
    "Cela dépend de ton niveau et de la formule choisie (nombre d'heures, boîte manuelle ou automatique). Le nombre d'heures et le contenu de chaque formule sont affichés avant ta réservation, et l'auto-école te propose un planning dès ton premier appel."],
  ["faq-gratuit", "C'est vraiment gratuit ?",
    "Oui, 100 % gratuit. Tu ne paies rien sur PlanPermis. Seule ta formation se paie, directement à l'auto-école, au prix affiché dans l'offre."],
  ["faq-documents", "Quels documents dois-je fournir ?",
    "Presque rien ! Si tu as déjà le code de la route, tu ajoutes simplement ton relevé de résultat du code (photo ou PDF). Sinon, aucun document. Pas de pièce d'identité ni de justificatif de domicile à envoyer."],
  ["faq-cpf", "Le permis accéléré est-il finançable avec le CPF ?",
    "Le permis B peut être financé par le CPF lorsque l'auto-école est certifiée Qualiopi et référencée sur moncompteformation.gouv.fr. Demande directement à ton auto-école partenaire si sa formule est éligible lors de ton premier appel."],
  ["faq-repasser", "J'ai raté mon permis : comment le repasser rapidement ?",
    "Choisis une formule accélérée courte (nous recommandons au moins 10 heures de conduite en 2 semaines) pour t'entraîner sur le parcours d'examen. Nos auto-écoles partenaires te proposent une date d'examen rapidement, avec l'accompagnement inclus dans les forfaits courts. Ton code reste valable 5 ans."],
  ["faq-neph", "C'est quoi le numéro NEPH ?",
    "C'est ton numéro de dossier permis (12 chiffres), créé quand tu t'inscris pour la première fois au permis. Tu le trouves sur ta convocation au code ou sur ton compte ANTS. Tu n'en as pas ? Aucun souci, l'auto-école s'en occupe avec toi."],
  ["faq-ecole", "Quand vais-je connaître mon auto-école ?",
    "Dès ta réservation : tu reçois par e-mail son nom, son adresse et son téléphone. C'est toi qui l'appelles, sous 2 jours, pour finaliser ton inscription."],
  ["faq-2jours", "Pourquoi dois-je appeler l'auto-école rapidement ?",
    "Derrière chaque formule, il y a une auto-école qui n'a parfois qu'une seule place : appeler vite, c'est la garder pour toi. Sans nouvelles, ta place est proposée à un autre élève, et on t'envoie un petit rappel avant. Pour que chacun ait sa chance, tu peux faire 2 réservations maximum sur 3 mois."],
  ["faq-places", "Pourquoi y a-t-il un nombre limité de places ?",
    "Chaque auto-école partenaire nous garde quelques places en accéléré par mois. Quand elles sont prises, il faut attendre le mois suivant, ou t'inscrire sur la liste d'attente pour être prévenu."],
];

export default function Accueil() {
  const { ville } = useParams();
  const cityInfo = ville ? (SEO_DATA.cities[ville] || {
    slug: ville, name: cityLabel(ville), path: `/permis/${ville}`,
    title: `Permis accéléré à ${cityLabel(ville)} | PlanPermis`,
    description: `Permis accéléré à ${cityLabel(ville)} : réserve gratuitement ta place dans une auto-école agréée partenaire.`,
    intro: `Tu veux passer ton permis rapidement à ${cityLabel(ville)} ? Compare les offres de nos auto-écoles partenaires et réserve ta place gratuitement en 3 minutes.`,
  }) : null;
  const seo = cityInfo || SEO_DATA.pages["/"];
  const faq = buildFaq(cityInfo?.name);
  const reserveTo = cityInfo ? `/reserver?ville=${encodeURIComponent(cityInfo.name)}` : "/reserver";
  useSeo({
    title: seo.title,
    description: seo.description,
    path: cityInfo ? cityInfo.path : "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "Organization", "@id": `${SEO_DATA.site}/#org`, name: "PlanPermis", url: SEO_DATA.site, logo: `${SEO_DATA.site}/logo512.png`, areaServed: "FR" },
        { "@type": "WebSite", "@id": `${SEO_DATA.site}/#website`, url: SEO_DATA.site, name: "PlanPermis", inLanguage: "fr-FR", publisher: { "@id": `${SEO_DATA.site}/#org` } },
        {
          "@type": "Service", name: cityInfo ? `Permis accéléré à ${cityInfo.name}` : "Permis accéléré",
          serviceType: "Réservation de place en permis de conduire accéléré",
          provider: { "@id": `${SEO_DATA.site}/#org` },
          areaServed: cityInfo ? { "@type": "City", name: cityInfo.name } : { "@type": "Country", name: "France" },
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR", description: "Réservation gratuite pour l'élève" },
        },
        { "@type": "FAQPage", mainEntity: faq.map(([, q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
        ...(cityInfo ? [{ "@type": "BreadcrumbList", itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: `${SEO_DATA.site}/` },
          { "@type": "ListItem", position: 2, name: "Permis accéléré", item: `${SEO_DATA.site}/permis-accelere` },
          { "@type": "ListItem", position: 3, name: cityInfo.name, item: `${SEO_DATA.site}${cityInfo.path}` },
        ] }] : []),
      ],
    },
  });
  const stats = useReviewStats();
  const [cities, setCities] = useState([]);
  useEffect(() => {
    api.get("/public/booking/cities").then(({ data }) => setCities((data.cities || []).filter((c) => c.remaining > 0))).catch(() => {});
  }, []);
  // Image du hero : personnalisable depuis l'admin (Site & pop-ups), sinon image par défaut.
  const [heroImg, setHeroImg] = useState(null);
  useEffect(() => {
    api.get("/public/site")
      .then(({ data }) => setHeroImg(mediaUrl(data.hero_image_url) || HERO_DEFAULT_IMG))
      .catch(() => setHeroImg(HERO_DEFAULT_IMG));
  }, []);
  const shownCities = cityInfo
    ? cities.filter((c) => (c.city || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") === cityInfo.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""))
    : cities;
  const totalPlaces = shownCities.reduce((n, c) => n + (c.remaining || 0), 0);
  const monthLabel = cities[0]?.month_label;

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <main className="flex-1">
        {/* HERO — une promesse, une action */}
        <section className="bg-ink px-6 pb-16 pt-10 text-white sm:px-12 sm:pt-14 lg:px-20 lg:py-20" data-testid="home-hero">
          <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
            <div>
              {stats.count > 0 ? (
                <div className="mb-5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 ring-1 ring-white/15" data-testid="hero-rating">
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                  <span className="text-xs font-bold">{Number(stats.average).toFixed(1).replace(".", ",")}/5 · {stats.count} avis vérifiés</span>
                </div>
              ) : (
                <div className="mb-5 inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 px-3 py-1.5 ring-1 ring-emerald-300/30" data-testid="hero-free-badge">
                  <BadgeCheck className="h-4 w-4 text-emerald-300" />
                  <span className="text-xs font-bold text-emerald-100">100 % gratuit pour les élèves</span>
                </div>
              )}
              <h1 className="text-[2.35rem] font-black leading-[1.05] tracking-tight sm:text-5xl" data-testid="hero-title">
                {cityInfo ? <>Permis accéléré à {cityInfo.name}, <span className="text-brand">sans attendre des mois.</span></> : <>Ton permis accéléré, <span className="text-brand">sans attendre des mois.</span></>}
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-neutral-300">
                {cityInfo ? cityInfo.intro : <>Nos auto-écoles partenaires agréées nous gardent des places en permis accéléré chaque mois. Choisis ta ville, compare les offres
                et réserve ta place <strong className="text-white">en 3 minutes</strong>.</>}
              </p>

              <ul className="mt-6 grid gap-2.5 text-sm text-neutral-200 sm:grid-cols-3">
                {["0 € pour toi", "Sans paiement en ligne", "Auto-écoles agréées"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-400/20"><Check className="h-3.5 w-3.5 text-emerald-300" /></span>{t}</li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <CtaButton testId="hero-cta" to={reserveTo} />
                <a href="#fonctionnement" className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-bold text-neutral-300 underline-offset-4 hover:text-white hover:underline">
                  Comment ça marche ?
                </a>
              </div>

              {totalPlaces > 0 && (
                <p className="mt-5 flex items-center gap-2 text-sm text-neutral-300" data-testid="hero-live-places">
                  <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" /></span>
                  <span><strong className="text-white">{totalPlaces} place{totalPlaces > 1 ? "s" : ""} disponible{totalPlaces > 1 ? "s" : ""}</strong>{monthLabel ? ` en ${monthLabel}` : ""} · {shownCities.slice(0, 3).map((c) => c.city).join(", ")}{shownCities.length > 3 ? "…" : ""}</span>
                </p>
              )}
            </div>

            <div className="relative hidden sm:block">
              <div className="overflow-hidden rounded-3xl ring-1 ring-white/10">
                {heroImg ? (
                  <img src={heroImg} alt="Élève qui récupère les clés après son permis" className="h-full max-h-[520px] w-full object-cover" loading="eager" data-testid="hero-image" />
                ) : (
                  <div className="h-[520px] w-full animate-pulse bg-white/5" />
                )}
              </div>
              <div className="absolute -bottom-5 -left-3 flex items-center gap-3 rounded-2xl bg-white px-4 py-3 text-ink shadow-xl sm:-left-6">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand/10 text-brand"><Timer className="h-5 w-5" /></span>
                <span>
                  <span className="block text-sm font-black leading-tight">Place réservée en 3 minutes</span>
                  <span className="block text-xs text-neutral-500">Ville → formule → tes infos</span>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* POUR QUI — 4 profils, par ordre de priorité */}
        <section className="px-5 py-10 sm:px-12 sm:py-14 lg:px-20" data-testid="home-audience">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-center text-2xl font-black tracking-tight text-ink sm:text-4xl">{"C'est pour toi si…"}</h2>
            <ol className="mt-6 grid gap-3 sm:mt-8 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
              {AUDIENCES.map((a, i) => (
                <li key={a.id} data-testid={`audience-${a.id}`}>
                  <Link to={a.to} data-testid={a.testId}
                    className={`group flex h-full gap-3.5 rounded-2xl border p-4 transition-colors hover:border-brand sm:flex-col sm:gap-0 sm:rounded-3xl sm:p-6 ${i === 0 ? "border-amber-200 bg-amber-50" : "border-neutral-200 bg-white"}`}>
                    <span className="text-2xl leading-none sm:text-3xl" aria-hidden="true">{a.emoji}</span>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <h3 className="text-base font-black leading-snug text-ink sm:mt-3 sm:text-lg">{a.title}</h3>
                      <p className="mt-1 flex-1 text-[13px] leading-relaxed text-neutral-600 sm:mt-1.5 sm:text-sm">{a.text}</p>
                      <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-brand sm:mt-4">{a.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
                    </div>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* COMMENT ÇA MARCHE — le parcours de l'élève, en 4 étapes */}
        <section id="fonctionnement" className="scroll-mt-20 bg-[#F6F8FC] px-6 py-16 sm:px-12 lg:px-20" data-testid="home-steps">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand">Comment ça marche</p>
              <h2 className="mt-2 text-3xl font-black tracking-tight text-ink sm:text-4xl">4 étapes, et tu es inscrit</h2>
              <p className="mt-3 text-neutral-600">Les 3 premières se font ici, en ligne. La dernière, c'est un simple coup de fil.</p>
            </div>
            <ol className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <Step n={1} Icon={MapPin} title="Choisis ta ville"
                sub="Tu vois tout de suite où il reste des places en accéléré ce mois-ci." />
              <Step n={2} Icon={ListChecks} title="Compare les offres"
                sub="Prix, nombre d'heures, boîte manuelle ou auto : tu prends la formule qui te va." />
              <Step n={3} Icon={UserRound} title="Laisse tes infos"
                sub="Nom, téléphone, e-mail. Si tu as déjà le code, ajoute juste ton relevé de résultat." />
              <Step n={4} Icon={PhoneCall} title="Appelle ton auto-école"
                sub="Tu reçois ses coordonnées par e-mail : appelle-la sous 2 jours pour finaliser ton inscription." />
            </ol>
          </div>
        </section>

        {/* PRIX — clair et sans ambiguïté */}
        <section id="tarifs" className="scroll-mt-20 px-6 py-16 sm:px-12 lg:px-20" data-testid="pricing-section">
          <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">
            <div className="overflow-hidden rounded-3xl ring-1 ring-neutral-200">
              <img src={REASSURE_IMG} alt="Leçon de conduite avec un moniteur" className="h-full max-h-[440px] w-full object-cover" loading="lazy" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand">Combien ça coûte ?</p>
              <h2 className="mt-2 text-3xl font-black tracking-tight text-ink sm:text-4xl">PlanPermis, c'est 0 € pour toi.</h2>
              <p className="mt-3 text-neutral-600">
                Tu ne paies rien sur le site. Tu règles uniquement ta formation, <strong>directement à l'auto-école</strong>,
                au prix affiché dans l'offre que tu choisis.
              </p>
              <ul className="mt-7 space-y-4">
                {[
                  [Wallet, "Aucun paiement en ligne", "Ni frais de dossier, ni acompte. Le prix de l'offre est celui de l'auto-école."],
                  [CalendarCheck, "Des places gardées chaque mois", "Les auto-écoles partenaires nous réservent des places en accéléré : pas besoin de les démarcher une par une."],
                  [ShieldCheck, "Des auto-écoles agréées", "Chaque partenaire est agréé par la préfecture et signe un contrat avec nous."],
                ].map(([Icon, t, d]) => (
                  <li key={t} className="flex gap-4">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-emerald-50 text-emerald-600"><Icon className="h-5 w-5" /></span>
                    <span><span className="block font-bold text-ink">{t}</span><span className="mt-0.5 block text-sm text-neutral-600">{d}</span></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* VILLES COUVERTES (masqué si aucune ville) */}
        <CitiesCovered />

        {/* AVIS ÉLÈVES (masqué tant qu'il n'y a pas de vrais avis) */}
        <ReviewsSection />

        {/* BON D'ACHAT 25 € */}
        <section className="px-6 py-10 sm:px-12 lg:px-20">
          <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-7 shadow-sm sm:p-9">
            <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold text-white">Cadeau de réussite</span>
                <h2 className="mt-3 text-2xl font-black text-ink sm:text-3xl">25 € offerts quand tu as ton permis 🎉</h2>
                <p className="mt-2 max-w-xl text-sm text-neutral-600">Tu es passé par PlanPermis et tu as réussi ? Demande ton bon d'achat de 25 € (une demande par personne).</p>
              </div>
              <Link to="/bon-carburant" data-testid="home-bon-carburant-cta"
                className="shrink-0 rounded-2xl bg-emerald-600 px-6 py-3.5 text-base font-bold text-white shadow-lg transition hover:bg-emerald-700">
                Demander mon bon 25 €
              </Link>
            </div>
          </div>
        </section>

        {/* CONTENU SEO — permis accéléré (+ maillage interne vers les villes) */}
        <section className="bg-[#F6F8FC] px-6 py-16 sm:px-12 lg:px-20" data-testid="home-seo-content">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-3xl font-black tracking-tight text-ink sm:text-4xl">
              {cityInfo ? `Passer son permis accéléré à ${cityInfo.name}` : "Le permis accéléré, comment ça marche ?"}
            </h2>
            <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-neutral-700">
              <p>
                {cityInfo
                  ? `À ${cityInfo.name}${cityInfo.deptName ? ` et dans le département ${cityInfo.deptName === cityInfo.name ? "" : `du ${cityInfo.deptName} `}(${cityInfo.dept})` : ""}, les délais pour obtenir une place en auto-école et une date d'examen peuvent atteindre plusieurs mois. Avec PlanPermis, tu accèdes directement aux places en permis accéléré que nos auto-écoles partenaires agréées réservent chaque mois pour les élèves pressés.`
                  : "Le permis accéléré, aussi appelé stage intensif de conduite, consiste à regrouper tes heures de conduite sur une courte période. Au lieu d'une ou deux leçons par semaine pendant des mois, tu conduis plusieurs heures par jour avec un enseignant diplômé : tu progresses plus vite et tu gardes tes automatismes jusqu'à l'examen."}
              </p>
              <p>
                {"Le problème, c'est de trouver une auto-école qui a encore de la place. PlanPermis travaille avec des auto-écoles agréées par la préfecture qui nous gardent des places en formation accélérée chaque mois. Tu compares les formules (nombre d'heures de conduite, boîte manuelle ou automatique, code de la route inclus ou non), tu réserves gratuitement en 3 minutes, et tu reçois aussitôt les coordonnées de ton auto-école pour démarrer."}
              </p>
              <p>
                {"Le permis accéléré s'adresse surtout aux élèves qui ont raté leur examen et veulent le repasser vite, à ceux qui ont perdu tous leurs points et doivent tout repasser, à ceux qui ont une urgence (nouveau travail, voyage, naissance) et aux conducteurs qui ont appris en double commande ou en conduite accompagnée et sont fatigués d'attendre une date d'examen. Le prix est celui fixé par l'auto-école et affiché avant ta réservation : rien n'est payé sur PlanPermis. "}
                <Link to="/permis-accelere" className="font-bold text-brand hover:underline">{"Tout savoir sur le permis accéléré (durée, prix, CPF)"}</Link>.
              </p>
            </div>
            <h3 className="mt-10 text-lg font-black text-ink">Permis accéléré dans ta ville</h3>
            <ul className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="seo-city-links">
              {Object.values(SEO_DATA.cities).map((c) => (
                <li key={c.slug}>
                  <Link to={c.path} className={`block rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${cityInfo?.slug === c.slug ? "border-brand bg-brand/10 text-brand" : "border-neutral-200 bg-white text-neutral-700 hover:border-brand hover:text-brand"}`}>
                    Permis accéléré {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ — réponses courtes */}
        <section id="faq" className="scroll-mt-20 px-6 py-16 sm:px-12 lg:px-20" data-testid="home-faq">
          <div className="mx-auto max-w-3xl">
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand">Questions fréquentes</p>
              <h2 className="mt-2 text-3xl font-black tracking-tight text-ink sm:text-4xl">Tu te poses une question ?</h2>
            </div>
            <div className="mt-8">
              {faq.map(([id, q, a]) => <FAQ key={id} q={q} testId={id}>{a}</FAQ>)}
            </div>
            <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl bg-ink px-6 py-6 text-center text-white sm:flex-row sm:justify-between sm:text-left">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-white/10"><Phone className="h-5 w-5" /></span>
                <span>
                  <span className="block font-bold">Une autre question ?</span>
                  <span className="block text-sm text-neutral-300">On te répond rapidement, par message ou par téléphone.</span>
                </span>
              </div>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-bold text-ink hover:bg-neutral-100">
                Nous écrire <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-6 text-center text-sm text-neutral-500" data-testid="faq-review">
              {"Tu as déjà trouvé ta place avec nous ? "}
              <LeaveReviewButton label="Laisse ton avis" className="font-bold text-brand underline-offset-2 hover:underline" />
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="bg-brand px-6 py-14 text-center text-white sm:px-12" data-testid="home-final-cta">
          <h2 className="text-3xl font-black tracking-tight sm:text-4xl">Prêt à passer ton permis plus vite ?</h2>
          <p className="mx-auto mt-3 max-w-xl text-white/85">Regarde les places disponibles près de chez toi. Ça prend 3 minutes et c'est gratuit.</p>
          <Link to={reserveTo} data-testid="final-cta" className="mt-7 inline-flex items-center gap-2 rounded-xl bg-white px-7 py-4 text-base font-bold text-brand shadow-lg hover:bg-neutral-100">
            {MAIN_CTA} <ArrowRight className="h-5 w-5" />
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
